import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { MDRBadge } from "@/components/MDRBadge";
import { toast } from "sonner";
import {
  pharmaciesData, getStructureById
} from "@/data/platformData";
import {
  Settings, ArrowLeft, Save, Building2, MapPin, Mail, Phone, Hospital
} from "lucide-react";

const STRUCTURE_ID = "str1";

export const StructureConfigurationPage = () => {
  const navigate = useNavigate();
  const structure = getStructureById(STRUCTURE_ID);

  const [info, setInfo] = useState({
    name: structure.name,
    address: structure.address,
    email: structure.email,
    phone: structure.phone,
    fiscalCode: structure.fiscalCode,
    vatNumber: structure.vatNumber
  });

  const [pharmaciesAssoc, setPharmaciesAssoc] = useState(
    pharmaciesData.map(p => ({ ...p, associated: structure.associatedPharmacies.includes(p.id) }))
  );

  const togglePharmacy = (id) => {
    setPharmaciesAssoc(prev => prev.map(p => p.id === id ? { ...p, associated: !p.associated } : p));
  };

  const save = () => {
    const associated = pharmaciesAssoc.filter(p => p.associated).length;
    toast.success("Configurazione salvata", { description: `${associated} farmacie convenzionate.` });
  };

  return (
    <div className="p-4 md:p-8 max-w-5xl mx-auto space-y-6">
      <div>
        <Button variant="ghost" size="sm" className="mb-2 -ml-2" onClick={() => navigate("/structure")}>
          <ArrowLeft className="h-4 w-4 mr-1" /> Panoramica
        </Button>
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <div className="flex items-center gap-2 mb-1">
              <Settings className="h-4 w-4 text-cyan-600" />
              <span className="text-xs uppercase tracking-wider text-muted-foreground">Configurazione</span>
              <MDRBadge variant="admin" size="sm" />
            </div>
            <h1 className="text-2xl md:text-3xl font-bold tracking-tight">Anagrafica e convenzioni</h1>
            <p className="text-sm text-muted-foreground mt-1">{structure.name}</p>
          </div>
          <Button variant="premium" onClick={save} className="gap-2" data-testid="structure-config-save-btn">
            <Save className="h-4 w-4" /> Salva
          </Button>
        </div>
      </div>

      <Tabs defaultValue="info" className="space-y-4">
        <TabsList>
          <TabsTrigger value="info" data-testid="structure-config-tab-info">Anagrafica</TabsTrigger>
          <TabsTrigger value="pharmacies" data-testid="structure-config-tab-pharmacies">Farmacie convenzionate</TabsTrigger>
        </TabsList>

        <TabsContent value="info">
          <Card>
            <CardHeader>
              <CardTitle className="text-base flex items-center gap-2">
                <Hospital className="h-4 w-4 text-cyan-600" /> Dati struttura
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid gap-3 md:grid-cols-2">
                <div>
                  <Label>Ragione sociale</Label>
                  <Input value={info.name} onChange={(e) => setInfo({ ...info, name: e.target.value })} data-testid="structure-config-name" />
                </div>
                <div>
                  <Label>Indirizzo</Label>
                  <Input value={info.address} onChange={(e) => setInfo({ ...info, address: e.target.value })} />
                </div>
                <div>
                  <Label>Email</Label>
                  <Input type="email" value={info.email} onChange={(e) => setInfo({ ...info, email: e.target.value })} />
                </div>
                <div>
                  <Label>Telefono</Label>
                  <Input value={info.phone} onChange={(e) => setInfo({ ...info, phone: e.target.value })} />
                </div>
                <div>
                  <Label>Codice Fiscale</Label>
                  <Input value={info.fiscalCode} onChange={(e) => setInfo({ ...info, fiscalCode: e.target.value })} />
                </div>
                <div>
                  <Label>Partita IVA</Label>
                  <Input value={info.vatNumber} onChange={(e) => setInfo({ ...info, vatNumber: e.target.value })} />
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="pharmacies">
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base flex items-center gap-2">
                <Building2 className="h-4 w-4 text-indigo-600" /> Farmacie convenzionate
              </CardTitle>
              <p className="text-xs text-muted-foreground">Solo le farmacie attivate possono inviare esami a questa struttura</p>
            </CardHeader>
            <CardContent className="space-y-2">
              {pharmaciesAssoc.map(p => (
                <div key={p.id} className="flex items-center justify-between p-3 rounded-lg border bg-card" data-testid={`pharmacy-row-${p.id}`}>
                  <div className="min-w-0">
                    <p className="font-medium text-sm">{p.name}</p>
                    <div className="flex flex-wrap gap-3 text-xs text-muted-foreground mt-1">
                      <span className="inline-flex items-center gap-1"><MapPin className="h-3 w-3" /> {p.address}</span>
                      <span className="inline-flex items-center gap-1"><Mail className="h-3 w-3" /> {p.email}</span>
                      <span className="inline-flex items-center gap-1"><Phone className="h-3 w-3" /> {p.phone}</span>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    {p.associated && <Badge variant="outline" className="bg-emerald-50 text-emerald-700 border-emerald-200">Convenzionata</Badge>}
                    <Switch checked={p.associated} onCheckedChange={() => togglePharmacy(p.id)} data-testid={`pharmacy-toggle-${p.id}`} />
                  </div>
                </div>
              ))}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default StructureConfigurationPage;
